import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import heroBanner from '@/assets/hero-banner.jpg';

const ROIStrip = () => {
  const [teamSize, setTeamSize] = useState(10);
  const [hourlyCost, setHourlyCost] = useState(115);
  const [monetizedPct, setMonetizedPct] = useState(70);

  const calc = useMemo(() => {
    const recoveredHoursPerMonth = 7;
    const totalRecoveredHours = teamSize * recoveredHoursPerMonth * 12;
    const annualValue = Math.round(totalRecoveredHours * hourlyCost * (monetizedPct / 100));
    const platformCost = teamSize * 69 * 12;
    const netValue = annualValue - platformCost;
    const roiMultiple = platformCost > 0 ? (annualValue / platformCost).toFixed(1) : '0';
    return { annualValue, platformCost, netValue, roiMultiple };
  }, [teamSize, hourlyCost, monetizedPct]);

  const fmt = (n: number) =>
    n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

  return (
    <section className="relative overflow-hidden py-16 md:py-20">
      {/* Cinematic background */}
      <div className="absolute inset-0 -z-10">
        <img src={heroBanner} alt="" className="h-full w-full object-cover scale-110" />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background/85 to-background" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-primary/5" />
      </div>

      {/* Floating glow orbs */}
      <div className="absolute top-20 left-1/4 h-72 w-72 rounded-full bg-primary/10 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-20 right-1/4 h-56 w-56 rounded-full bg-primary/8 blur-[100px] pointer-events-none" />

      <div className="relative mx-auto max-w-6xl px-6 md:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-1.5 mb-6">
            <div className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
            <span className="text-xs font-medium text-primary tracking-wide uppercase">
              Live Calculator
            </span>
          </div>
          <h2 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
            Model your <span className="text-gradient">agentic AI ROI</span>
          </h2>
          <p className="mx-auto mt-5 max-w-lg text-muted-foreground text-lg">
            Estimate recovered engineering capacity from autonomous AI agents and justify budget in
            under a minute.
          </p>
        </motion.div>

        {/* Calculator */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.15 }}
          className="grid gap-0 lg:grid-cols-[1fr_1px_1fr] rounded-3xl border border-border overflow-hidden"
          style={{
            background: 'var(--gradient-card)',
            boxShadow: 'var(--shadow-glow), var(--shadow-card)',
          }}
        >
          {/* Left — Sliders */}
          <div className="p-8 md:p-10 space-y-10">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-widest text-primary mb-8">
                Your inputs
              </h3>
              <div className="space-y-8">
                <SliderInput
                  label="Engineering team size"
                  value={teamSize}
                  onChange={setTeamSize}
                  min={1}
                  max={100}
                  display={`${teamSize} developers`}
                />
                <SliderInput
                  label="Blended hourly cost"
                  value={hourlyCost}
                  onChange={setHourlyCost}
                  min={50}
                  max={300}
                  display={`$${hourlyCost}/hr`}
                />
                <SliderInput
                  label="Recovered hours monetized"
                  value={monetizedPct}
                  onChange={setMonetizedPct}
                  min={10}
                  max={100}
                  display={`${monetizedPct}%`}
                />
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-xl bg-muted/50 border border-border p-4">
              <div className="mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 border-primary/40 flex items-center justify-center">
                <div className="h-1.5 w-1.5 rounded-full bg-primary" />
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Based on 7 recovered engineering hours per developer per month through autonomous
                AI agent workflows.
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="hidden lg:block bg-border" />
          <div className="lg:hidden h-px bg-border" />

          {/* Right — Results */}
          <div className="p-8 md:p-10 flex flex-col">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-primary mb-8">
              Projected impact
            </h3>

            <div className="flex-1 space-y-6">
              <ResultRow label="Annual value recovered" value={fmt(calc.annualValue)} />
              <ResultRow label="Platform cost (Growth tier)" value={fmt(calc.platformCost)} muted />
              <div className="border-t border-border pt-6">
                <ResultRow label="Net new value" value={fmt(calc.netValue)} large />
              </div>
            </div>

            {/* ROI callout */}
            <motion.div
              key={calc.roiMultiple}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              className="mt-8 relative rounded-2xl overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-primary/5 to-transparent" />
              <div className="absolute inset-0 border border-primary/20 rounded-2xl" />
              <div className="relative p-8 text-center">
                <p className="text-xs font-semibold uppercase tracking-widest text-primary/70 mb-2">
                  Return on investment
                </p>
                <AnimatePresence mode="wait">
                  <motion.span
                    key={calc.roiMultiple}
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -10, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="block text-6xl font-black text-gradient tabular-nums"
                  >
                    {calc.roiMultiple}x
                  </motion.span>
                </AnimatePresence>
              </div>
            </motion.div>

            <p className="mt-6 text-[11px] text-muted-foreground/60 text-center leading-relaxed">
              Planning estimate. Results depend on adoption and workflow maturity.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* ── Slider ── */
interface SliderInputProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  display: string;
}

const SliderInput = ({ label, value, onChange, min, max, display }: SliderInputProps) => {
  const pct = ((value - min) / (max - min)) * 100;

  return (
    <div>
      <div className="flex items-baseline justify-between mb-3">
        <span className="text-sm text-muted-foreground">{label}</span>
        <motion.span
          key={display}
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-sm font-bold text-foreground tabular-nums"
        >
          {display}
        </motion.span>
      </div>
      <div className="relative h-2 rounded-full bg-muted">
        <div
          className="absolute inset-y-0 left-0 rounded-full"
          style={{
            width: `${pct}%`,
            background: 'var(--gradient-accent)',
          }}
        />
        <input
          type="range"
          min={min}
          max={max}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 h-5 w-5 rounded-full bg-primary border-[3px] border-background shadow-lg shadow-primary/30 pointer-events-none transition-all"
          style={{ left: `calc(${pct}% - 10px)` }}
        />
      </div>
    </div>
  );
};

/* ── Result row ── */
const ResultRow = ({
  label,
  value,
  muted,
  large,
}: {
  label: string;
  value: string;
  muted?: boolean;
  large?: boolean;
}) => (
  <div className="flex items-center justify-between">
    <span className={`text-sm ${muted ? 'text-muted-foreground/60' : 'text-muted-foreground'}`}>
      {label}
    </span>
    <motion.span
      key={value}
      initial={{ opacity: 0, x: 8 }}
      animate={{ opacity: 1, x: 0 }}
      className={`font-bold tabular-nums ${
        large
          ? 'text-2xl text-foreground'
          : muted
            ? 'text-base text-muted-foreground/60'
            : 'text-lg text-foreground'
      }`}
    >
      {value}
    </motion.span>
  </div>
);

export default ROIStrip;
